import React from 'react';
import { Label, ILabelProps } from 'components/inputs/Label';
import { IPropertyChild, getPropertyDisplayLabel } from 'components/property-editors/utils/ProviderNodeUtils';
import { getEditorDataAttributeProps } from './utils/EditorDataAttributeUtils';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { getDataAttributes } from 'utils/DataAttributeUtils';
import classNames from 'classnames';
import { ContextPropertyNodeLabelPosition } from '@decisions/server/DecisionsFramework/Design/Properties/ContextPropertyNodeLabelPosition';

interface IEditorLabelProps extends ILabelProps {
  editor: IPropertyChild;
  path: string[];
  /**
   * True will cause the label's React.Children to be rendered in a fragment,
   * and data attributes and class name will be passed to the first child element.
   */
  hideInputLabel?: boolean;
  actions?: React.ReactNode;
}

/**
 * A label / wrapper element that assumes a few things about what we intend for editors.
 *
 * passing the `hideInputLabel` prop will cause the label's React.Children to be rendered in a
 * fragment, and data attributes and class name will be passed to the first child element.
 * This is also true if Node.DisplayLabelPosition is "None"
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function EditorLabel(props: IEditorLabelProps) {
  const dataAttrProps = getEditorDataAttributeProps(props.editor, props.path);
  const validation = getEditorValidationSummary(props.editor);
  const label = getPropertyDisplayLabel(props.editor);
  const editorName = props.editor.Node.MvcEditorName;

  if (props.hideInputLabel || props.editor.Node.DisplayLabelPosition === ContextPropertyNodeLabelPosition.None) {
    const children = React.Children.toArray(props.children);
    if (!children.length) {
      console.warn('you tried to render a hidden label with no children! this will do nothing.');
      return null;
    }
    const fieldChild = children.shift() as JSX.Element;
    const dataAttrs = getDataAttributes(dataAttrProps);
    const className = classNames(fieldChild.props.className, props.className, props.editor.className);
    return (
      <>
        {/* Think seriously before adding DOM here, as it will affect a _lot_ of things. Consider instead putting it outside */}
        {React.cloneElement(fieldChild, { className, ...dataAttrs })}
        {children}
      </>
    );
  }

  return (
    <Label
      {...props}
      {...dataAttrProps}
      validation={validation}
      label={label}
      data-editor-name={editorName}
      className={classNames(props.editor.className, props.className)}
    />
  );
}
